import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getJobs,
  getJobHistory,
  getJobSummaries,
  runJob,
  enableJob,
  disableJob,
  Job,
  JobRun,
  JobSummary,
  JobStatus,
} from '@/api/jobs.api';

/**
 * Hook to get all jobs
 */
export function useJobs() {
  return useQuery({
    queryKey: ['jobs'],
    queryFn: getJobs,
    staleTime: 1000 * 30, // 30 seconds
  });
}

/**
 * Hook to get job run history
 */
export function useJobHistory(options?: {
  jobName?: string;
  status?: JobStatus;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: ['jobs', 'history', options],
    queryFn: () => getJobHistory(options),
    staleTime: 1000 * 30, // 30 seconds
  });
}

/**
 * Hook to get job summaries
 */
export function useJobSummaries() {
  return useQuery({
    queryKey: ['jobs', 'summaries'],
    queryFn: getJobSummaries,
    staleTime: 1000 * 60, // 1 minute
  });
}

/**
 * Hook to run a job manually
 */
export function useRunJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobName: string) => runJob(jobName),
    onSuccess: () => {
      // Invalidate jobs and history
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
}

/**
 * Hook to enable a job
 */
export function useEnableJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobName: string) => enableJob(jobName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
}

/**
 * Hook to disable a job
 */
export function useDisableJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobName: string) => disableJob(jobName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
}

export type { Job, JobRun, JobSummary, JobStatus };
